import { FC, useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Plus, X } from "lucide-react";
import { getUserGroups, getRoleMappingRoles, registerUserGroups } from "../../../api/common";
import SubmitButton from "../../../Components/Button";
import CustomSelect from "../../../Components/Dropdown";
import Alert from "../../../Components/AlertPopup";
import styles from "./style.module.scss";

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  ADMIN: "Admin",
  AUDITOR: "Auditor",
  LEAD_AUDITOR: "Lead Auditor",
};

interface RoleMappingData {
  id?: number;
  role?: string;
  adGroupName?: string | string[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSaveSuccess?: (message: string) => void;
  mode: "add" | "edit";
  data?: RoleMappingData | null;
}

const AddEditRoleMappingModal: FC<Props> = ({
  show,
  onClose,
  onSaveSuccess,
  mode,
  data = null,
}) => {
  const [selectedRole, setSelectedRole] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [adGroups, setAdGroups] = useState<string[]>([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState<any[]>([]);
  const [adGroupOptions, setAdGroupOptions] = useState<{ label: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (show) {
      fetchRoles();
      fetchAdGroups();
      if (isEdit && data) {
        setSelectedRole(
          data.role
            ? [{ label: ROLE_DISPLAY_NAMES[data.role] || data.role, value: data.role }]
            : []
        );
        const groups = data.adGroupName
          ? (Array.isArray(data.adGroupName) ? data.adGroupName : [data.adGroupName])
          : [];
        setAdGroups(groups);
      } else {
        setSelectedRole([]);
        setAdGroups([]);
      }
      setSelectedAdGroup([]);
    }
  }, [show, mode, data]);

  const fetchRoles = async () => {
    try {
      const response = await getRoleMappingRoles();
      if (response && Array.isArray(response)) {
        const options = response.map((role: any) => {
          const value = role.name || role;
          return {
            label: ROLE_DISPLAY_NAMES[value] || value,
            value: value,
          };
        });
        setRoleOptions(options);
      }
    } catch {
      // keep options empty on error
    }
  };

  const fetchAdGroups = async () => {
    try {
      const response = await getUserGroups();
      if (response && Array.isArray(response)) {
        const options = response.map((group: any) => ({
          label: group.name || group,
          value: group.name || group,
        }));
        setAdGroupOptions(options);
      }
    } catch {
      // keep options empty on error
    }
  };

  const handleAddGroup = () => {
    if (selectedAdGroup.length > 0) {
      const groupName = selectedAdGroup[0].value;
      if (!adGroups.includes(groupName)) {
        setAdGroups((prev) => [...prev, groupName]);
      }
      setSelectedAdGroup([]);
    }
  };

  const handleRemoveGroup = (group: string) => {
    setAdGroups((prev) => prev.filter((g) => g !== group));
  };

  const role = selectedRole.length > 0 ? selectedRole[0].value : "";
  const isFormValid = role.trim() !== "" && adGroups.length > 0;

  const handleSave = async () => {
    if (!isFormValid) return;
    setSaving(true);
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    try {
      const response = await registerUserGroups(adGroups, role);
      setAlertMessage(response?.message || (isEdit ? "Role mapping updated successfully!" : "Role mapping saved successfully!"));
      setShowSuccessAlert(true);
      onSaveSuccess?.(response?.message || "Role mapping saved successfully");
    } catch (error: any) {
      const responseData = error?.response?.data;
      const apiMessage =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || responseData?.error;
      setAlertMessage(apiMessage || "Failed to save role mapping. Please try again.");
      setShowErrorAlert(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    onClose();
  };

  const title = isEdit ? "Edit Role Mapping" : "Add Role Mapping";

  return (
    <Modal show={show} onHide={onClose} centered className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalTitle}>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert
          show={showSuccessAlert}
          onClose={handleSuccessAlertClose}
          message={alertMessage}
        />
        <Alert
          type="error"
          show={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
          message={alertMessage}
        />

        {/* Role */}
        <Row className={styles.fieldRow}>
          <Col xs={12}>
            <Form.Label className={styles.fieldLabel}>Role</Form.Label>
          </Col>
          <Col xs={12}>
            <CustomSelect
              options={roleOptions}
              placeholder="Select role"
              onChange={(selected) => setSelectedRole(selected)}
              value={selectedRole}
              searchable={true}
              isDisabled={isEdit}
            />
          </Col>
        </Row>

        {/* AD Group */}
        <Row className={styles.fieldRow}>
          <Col xs={12}>
            <div className={styles.adGroupRow}>
              <div className={styles.adGroupSelect}>
                <CustomSelect
                  options={adGroupOptions.filter(
                    (opt) => !adGroups.includes(opt.value)
                  )}
                  placeholder="Enter AD group name(s)"
                  onChange={(selected) => setSelectedAdGroup(selected)}
                  value={selectedAdGroup}
                  searchable={true}
                />
              </div>
              <button
                type="button"
                className={styles.addBtn}
                onClick={handleAddGroup}
                disabled={selectedAdGroup.length === 0}
              >
                <Plus size={18} />
              </button>
            </div>
          </Col>
        </Row>

        {/* AD Groups List */}
        <Row className={styles.fieldRow}>
          <Col xs={12}>
            <div className={styles.groupList}>
              {adGroups.length === 0 ? (
                <div className={styles.noGroups}>No AD Groups added</div>
              ) : (
                adGroups.map((group) => (
                  <div key={group} className={styles.groupChip}>
                    <span>{group}</span>
                    <X
                      size={14}
                      className={styles.removeIcon}
                      onClick={() => handleRemoveGroup(group)}
                    />
                  </div>
                ))
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <SubmitButton type="secondary" onClick={onClose} className={styles.cancelBtn}>
          Cancel
        </SubmitButton>
        <SubmitButton disabled={!isFormValid || saving} onClick={handleSave} className={styles.saveBtn}>
          {saving ? "Saving..." : "Save"}
        </SubmitButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditRoleMappingModal;
